import { useEffect, useState } from "react";
import { Drawer, Button, Spin, Row, Col, notification } from "antd";
import Icon from "@ant-design/icons";
import { LocationPinIcon } from "../../assets/icons/icons";
import "./ConfirmationModal.scss";

interface LocationConfirmationModalProps {
  open: boolean;
  location: {
    primary: string;
    secondary?: string | null;
    barcode: string;
    status?: "In" | "Out" | null;
  } | null;
  lastScannedLocation?: string | null;
  lastLocationStatus?: "In" | "Out" | null;
  onConfirm: (locationType?: "In" | "Out") => void;
  onCancel: () => void;
  isLoading?: boolean;
  secondaryLocationActions?: Map<string, "In" | "Out">;
}

const LocationConfirmationModal = ({
  open,
  location,
  lastScannedLocation,
  lastLocationStatus,
  onConfirm,
  onCancel,
  isLoading = false,
  secondaryLocationActions,
}: LocationConfirmationModalProps) => {
  const lastLocation = lastScannedLocation || "";
  const parts = lastLocation.split("•").map((p) => p.trim());
  const lastPrimaryRaw = parts[0] || "";
  const lastSecondaryRaw = parts[1] || "";
  const lastPrimary = lastPrimaryRaw ? lastPrimaryRaw.trim() : "";
  const lastTypeMatch = lastPrimary.match(/\((In|Out)\)/i);
  const lastType = lastTypeMatch
    ? (lastTypeMatch[1] as "In" | "Out")
    : undefined;
  const normalizedLastTypeFromString = lastType?.toLowerCase();
  const normalizedLastStatus = lastLocationStatus
    ? lastLocationStatus.toLowerCase()
    : undefined;
  const normalizedLastPrimary = lastPrimary
    .replace(/\((In|Out)\)/i, "")
    .trim()
    .toLowerCase();
  const normalizedLastSecondary = lastSecondaryRaw
    .replace(/\((In|Out)\)/i, "")
    .trim()
    .toLowerCase();
  const hasLastLocation = Boolean(
    (lastScannedLocation && lastScannedLocation.trim().length) ||
      normalizedLastStatus
  );
  const selectedPrimary = location?.primary?.trim().toLowerCase();
  const selectedSecondary = location?.secondary?.trim().toLowerCase() || "";
  const isSamePrimary =
    Boolean(selectedPrimary) &&
    Boolean(normalizedLastPrimary) &&
    selectedPrimary === normalizedLastPrimary;
  const isSameSecondary =
    (selectedSecondary || "") === (normalizedLastSecondary || "");
  const isSameLocation = isSamePrimary && isSameSecondary;

  const hasLocation = Boolean(location);
  const normalizedLocationStatus = location?.status
    ? location.status.toLowerCase()
    : undefined;

  const baseLastType = normalizedLastTypeFromString ?? normalizedLastStatus;
  const effectiveLastType = isSameLocation
    ? baseLastType ?? normalizedLocationStatus
    : baseLastType;

  // Check if this is a secondary location and if an action was already performed
  const hasSecondary = Boolean(selectedSecondary);
  const secondaryKey =
    hasSecondary && location
      ? `${location.primary.toLowerCase()}|${selectedSecondary}`
      : null;
  const performedAction =
    secondaryKey && secondaryLocationActions
      ? secondaryLocationActions.get(secondaryKey)
      : null;

  // Determine button highlighting based on new requirements:
  // 1. For new secondary location (no previous action) → highlight "In"
  // 2. For same secondary location after "In" → highlight "Out"
  // 3. For same secondary location after "Out" → highlight "In" (if they want to do In again for a different context)
  const lastWasIn = effectiveLastType === "in" || performedAction === "In";
  const lastWasOut = effectiveLastType === "out" || performedAction === "Out";

  const nextActionUnavailable =
    hasSecondary && isSameLocation && performedAction === "Out";

  let highlightLocationIn = false;
  let highlightLocationOut = false;

  if (!nextActionUnavailable) {
    if (hasSecondary) {
      // For secondary locations
      if (!performedAction) {
        if (isSameLocation) {
          if (effectiveLastType === "in") {
            highlightLocationOut = true;
          } else if (effectiveLastType === "out") {
            highlightLocationIn = true;
          } else {
            highlightLocationIn = true;
          }
        } else {
          // Different location → treat as new
          highlightLocationIn = true;
        }
      } else if (performedAction === "In" && isSameLocation) {
        // Same location after In → highlight Out
        highlightLocationOut = true;
      } else if (performedAction === "Out" && isSameLocation) {
        // Same location after Out → highlight In (though this might not happen often)
        highlightLocationIn = true;
      } else {
        // Different location, treat as new
        highlightLocationIn = true;
      }
    } else {
      // For primary-only locations or no specific tracking
      if (!hasLastLocation) {
        highlightLocationIn = true;
      } else if (isSameLocation && lastWasIn) {
        highlightLocationOut = true;
      } else if (isSameLocation && lastWasOut) {
        highlightLocationIn = true;
      } else {
        highlightLocationIn = true;
      }
    }
  }

  const locationInButtonClass = highlightLocationIn
    ? "confirm-button active-location-button"
    : "location-in-button";
  const locationOutButtonClass = highlightLocationOut
    ? "confirm-button active-location-button"
    : "location-out-button";

  const locationDisplay = location
    ? [location.primary, location.secondary].filter(Boolean).join(" • ")
    : "N/A";

  const [pendingAction, setPendingAction] = useState<"In" | "Out" | null>(null);

  useEffect(() => {
    if (!isLoading) {
      setPendingAction(null);
    }
  }, [isLoading]);

  // Handle button click with validation
  const handleLocationInClick = () => {
    if (nextActionUnavailable) {
      notification.warning({
        message: "No Action Required",
        description: "This location already completed the required action.",
        duration: 3,
      });
      return;
    }
    if (hasSecondary && secondaryKey && performedAction === "In") {
      notification.error({
        message: "Action Already Performed",
        description: "Action already performed for this location.",
        duration: 3,
      });
      return;
    }
    setPendingAction("In");
    onConfirm("In");
  };

  const handleLocationOutClick = () => {
    if (nextActionUnavailable) {
      notification.warning({
        message: "No Action Required",
        description: "This location already completed the required action.",
        duration: 3,
      });
      return;
    }
    if (hasSecondary && secondaryKey && performedAction === "Out") {
      notification.error({
        message: "Action Already Performed",
        description: "Action already performed for this location.",
        duration: 3,
      });
      return;
    }
    setPendingAction("Out");
    onConfirm("Out");
  };

  return (
    <Drawer
      open={open}
      onClose={onCancel}
      className="confirmation-drawer location-drawer"
      placement="bottom"
      closable={false}
      mask={true}
      height="auto"
      destroyOnClose
      maskClosable={!isLoading}
    >
      <Spin spinning={isLoading} tip="Updating location...">
        <div className="confirmation-content">
          <h2 className="confirmation-title">Location Confirmation</h2>
          <p className="confirmation-subtitle">
            You're about to assign this tote to the following location:
          </p>

          <div className="info-card">
            {/* <div className="info-header"> */}
            <Row gutter={15} align="top">
              <Col className="iconBox" style={{ background: "#e6f3f7" }}>
                <Icon component={LocationPinIcon} className="info-icon" />
              </Col>
              <Col>
                <div className="labels">Location</div>
                <div className="info-value">{locationDisplay || "N/A"}</div>
              </Col>
            </Row>
            {/* </div> */}
          </div>

          <div className="modal-actions">
            <Button
              onClick={handleLocationInClick}
              className={locationInButtonClass}
              disabled={isLoading || nextActionUnavailable}
              loading={isLoading && pendingAction === "In"}
            >
              Location "In"
            </Button>
            <Button
              onClick={handleLocationOutClick}
              className={locationOutButtonClass}
              disabled={isLoading || nextActionUnavailable}
              loading={isLoading && pendingAction === "Out"}
            >
              Location "Out"
            </Button>
          </div>
          {nextActionUnavailable && (
            <div
              style={{
                marginTop: 12,
                textAlign: "center",
                fontSize: 12,
                color: "#8c8c8c",
              }}
            >
              No further action required for this secondary location.
            </div>
          )}
        </div>
      </Spin>
    </Drawer>
  );
};

export default LocationConfirmationModal;
